import React, { useEffect, useState } from 'react';
import { MdAddToHomeScreen } from 'react-icons/md';
import { FiShare } from 'react-icons/fi';

async function preloadPwaAssets(onProgress) {
  if (!('caches' in window)) return;

  const cache = await caches.open('mogym-install-cache-v1');

  let files = ['/', '/index.html', '/manifest.json', '/logo192.png', '/logo512.png'];

  try {
    const res = await fetch('/asset-manifest.json', { cache: 'no-store' });
    const manifest = await res.json();

    const manifestFiles = Object.values(manifest.files || {}).filter(
      (x) =>
        typeof x === 'string' &&
        (x.endsWith('.js') ||
          x.endsWith('.css') ||
          x.endsWith('.png') ||
          x.endsWith('.svg') ||
          x.endsWith('.json'))
    );

    files = [...files, ...manifestFiles];
  } catch {}

  const uniqueFiles = [...new Set(files.filter(Boolean))];

  let done = 0;

  for (const file of uniqueFiles) {
    try {
      const response = await fetch(file, { cache: 'reload' });

      if (response.ok) {
        await cache.put(file, response.clone());
      }
    } catch {}

    done += 1;
    onProgress(Math.round((done / uniqueFiles.length) * 100));
  }
}

export default function InstallPwaButton({
  label = 'نصب سریع موجیم روی موبایل',
  image = '/logo192.png',
}) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [progress, setProgress] = useState(0);

  const isIOS = /iphone|ipad|ipod/i.test(window.navigator.userAgent);
  const isStandalone =
    window.matchMedia?.('(display-mode: standalone)')?.matches ||
    window.navigator.standalone === true;

  useEffect(() => {
    if (isStandalone) {
      setInstalled(true);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    const installedHandler = () => {
      setInstalled(true);
      setInstalling(false);
      setProgress(100);
    };

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, [isStandalone]);

  const handleInstall = async () => {
    try {
      if (installing) return;

      if (deferredPrompt) {
        deferredPrompt.prompt();

        const result = await deferredPrompt.userChoice;

        if (result.outcome === 'accepted') {
          setInstalling(true);
          setProgress(0);

          await preloadPwaAssets(setProgress);

          setInstalling(false);
          setInstalled(true);
        }

        return;
      }

      setShowGuide(true);
    } catch {
      setInstalling(false);
      setShowGuide(true);
    }
  };

  if (installed) return null;

  return (
    <>
      <button
        type="button"
        onClick={handleInstall}
        disabled={installing}
        className="
          w-full
          max-w-md
          rounded-2xl
          border
          border-white/10
          bg-gradient-to-l
          from-[#2563eb]
          to-[#7c3aed]
          p-4
          text-white
          transition
          hover:scale-[1.01]
          disabled:opacity-80
          disabled:hover:scale-100
        "
      >
        <div className="flex items-center justify-center gap-4">
          <img
            src={image}
            alt="PWA"
            className="w-14 h-14 rounded-2xl"
          />

          <div className="text-right flex-1">
            <div className="font-black text-base">
              {label}
            </div>

            <div className="text-xs text-white/80 mt-1">
              {installing
                ? `در حال آماده‌سازی نصب... ${progress}%`
                : 'نصب روی تمامی سیستم عامل‌ها (iOS، Android و Windows)'}
            </div>

            {installing && (
              <div className="mt-3 h-2 rounded-full bg-white/20 overflow-hidden">
                <div
                  className="h-full bg-white rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            )}
          </div>
        </div>
      </button>

      {showGuide && (
        <div className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-4">
          <div className="bg-[#111827] text-white rounded-3xl max-w-md w-full p-6 border border-white/10">
            <h3 className="text-xl font-black mb-5 text-center">
              نصب موجیم روی موبایل
            </h3>

            {isIOS ? (
              <div className="space-y-3 text-sm leading-8 text-slate-300 text-right">
                <div className="flex items-center justify-end gap-2">
                  <span>
                    1. پایین مرورگر Safari روی دکمه Share بزن
                  </span>

                  <span className="w-9 h-9 rounded-xl bg-white flex items-center justify-center shrink-0">
                    <FiShare size={20} className="text-blue-500" />
                  </span>
                </div>

                <div className="flex items-center justify-end gap-2">
                  <span>
                    2. گزینه Add to Home Screen را انتخاب کن
                  </span>
                </div>
                <p>
                  3. روی Add بزن ✅
                </p>
              </div>
            ) : (
              <div className="space-y-3 text-sm leading-8 text-slate-300 text-right">
                <p>
                  1. منوی مرورگر (⋮) را باز کن
                </p>
                   <p>
                    2. گزینه Install App یا افزودن به صفحه اصلی یا Add to Home Screen را بزن
                </p>
                <p>
                  3. نصب را تایید کن ✅
                </p>
              </div>
            )}

            <button
              type="button"
              onClick={() => setShowGuide(false)}
              className="
                mt-6
                w-full
                py-3
                rounded-2xl
                bg-gradient-to-l
                from-blue-600
                to-purple-600
                font-bold
              "
            >
              متوجه شدم
            </button>
          </div>
        </div>
      )}
    </>
  );
}